import { PlusIcon } from '@heroicons/react/24/outline';
import { CustomCode } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Button from '@superfunnel/ui-core/v2/Button';
import { updateCustomCode } from '@superfunnel/utils';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DashboardContext } from '../../contexts/DashboardContext';
import { UserContext } from '../../contexts/UserContext';
import SettingSectionTitle from '../settings/SettingSectionTitle';
import CustomCodeCard from './CustomCodeCard';
import CustomCodeEditorModal from './CustomCodeEditorModal';

export interface AccountCustomCodesProps {
  /** Controlled mode: custom codes value */
  customCodes?: CustomCode[];
  /** Controlled mode: callback when custom codes change (won't save immediately) */
  onChange?: (codes: CustomCode[]) => void;
}

export const AccountCustomCodes = ({
  customCodes: controlledCodes,
  onChange,
}: AccountCustomCodesProps) => {
  const { selectedAccount } = useContext(UserContext);
  const { accountConfig, updateAccountConfig } = useContext(DashboardContext);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<CustomCode | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<string | undefined>();

  // Use controlled value if provided, otherwise fall back to context
  const isControlled = controlledCodes !== undefined;
  const accountCodes = useMemo(
    () => controlledCodes ?? accountConfig?.customCodes ?? [],
    [controlledCodes, accountConfig?.customCodes],
  );

  const updateCodes = useCallback(
    async (codes: CustomCode[]) => {
      if (isControlled && onChange) {
        // Controlled mode: just call onChange, don't save to Firestore
        onChange(codes);
      } else {
        // Uncontrolled mode: save immediately to Firestore
        updateAccountConfig({
          customCodes: codes as any,
        });
      }
    },
    [isControlled, onChange, updateAccountConfig],
  );

  const handleAddCode = () => {
    setEditingCode(undefined);
    setEditorOpen(true);
  };

  const handleEditCode = (code: CustomCode) => {
    setEditingCode(code);
    setEditorOpen(true);
  };

  const handleSaveCode = useCallback(
    async (code: CustomCode) => {
      const isNew = !accountCodes.find((c) => c.id === code.id);

      if (isNew) {
        // Add new code
        await updateCodes([...accountCodes, code]);
      } else {
        // Update existing code
        const updatedCodes = accountCodes.map((c) =>
          c.id === code.id ? code : c,
        );
        await updateCodes(updatedCodes);
      }
    },
    [accountCodes, updateCodes],
  );

  const handleToggleCode = useCallback(
    async (codeId: string, enabled: boolean) => {
      const updatedCodes = accountCodes.map((c) =>
        c.id === codeId ? updateCustomCode(c, { enabled }) : c,
      );
      await updateCodes(updatedCodes);
    },
    [accountCodes, updateCodes],
  );

  const handleDeleteCode = useCallback(
    async (codeId: string) => {
      const updatedCodes = accountCodes.filter((c) => c.id !== codeId);
      await updateCodes(updatedCodes);
      setDeleteConfirm(undefined);
    },
    [accountCodes, updateCodes],
  );

  return (
    <div>
      <SettingSectionTitle
        title="Custom Code"
        description="Manage multiple custom code snippets at the account level. These codes can be inherited by all pages."
      />

      <div className="mt-6 border-t border-gray-200 pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">
            Account Custom Codes
          </h3>
          <Button size="sm" onClick={handleAddCode}>
            <PlusIcon className="-ml-0.5 h-4 w-4" />
            Add Code
          </Button>
        </div>

        {accountCodes.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <p className="text-sm text-gray-500">
              No custom codes yet. Add your first custom code to get started.
              These codes will be automatically inherited by all pages.
              websites.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {accountCodes.map((code) => (
              <CustomCodeCard
                key={code.id}
                customCode={code}
                source="account"
                displayEnabled={code.enabled}
                onToggle={handleToggleCode}
                onEdit={handleEditCode}
                onDelete={(id) => setDeleteConfirm(id)}
              />
            ))}
          </div>
        )}
      </div>

      <CustomCodeEditorModal
        customCode={editingCode}
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingCode(undefined);
        }}
        onSave={handleSaveCode}
        userId={selectedAccount?.data().createdBy || 'unknown'}
      />

      <AlertModal
        title="Delete Custom Code"
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setDeleteConfirm(undefined),
          },
          {
            type: 'primary',
            color: 'negative',
            title: 'Delete',
            onClick: () => deleteConfirm && handleDeleteCode(deleteConfirm),
          },
        ]}
        type="warn"
      >
        <p>
          Are you sure you want to delete this custom code? This will affect all
          pages that inherit this code. This action cannot be undone.
        </p>
      </AlertModal>
    </div>
  );
};
